import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Tag, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
    TagOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/ui";

export const TaxesShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        id,
        meta: {
            fields: ["id", "taxTypeId", "taxType", "rate", "isActive", "createdAt", "updatedAt"]
        }
    });

    const { data, isLoading, isError } = queryResult;
    const record = data?.data;

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (isError || !record) {
        return (
            <div className="page-container">
                <PageHeader
                    title="Tax Not Found"
                    breadcrumbs={[
                        { title: "Taxes", href: "/taxes" },
                        { title: "Not Found" },
                    ]}
                />
                <div className="info-card">
                    <div className="info-card-body text-center py-8">
                        <p className="text-text-muted">Error loading tax information. Please try again.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <PageHeader
                title={record.taxType || "Tax Details"}
                titleIcon={<TagOutlined />}
                breadcrumbs={[
                    { title: "Taxes", href: "/taxes" },
                    { title: record.taxType || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/taxes/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Tax Rate</div>
                        <div className="mini-stat-value text-primary">{record.rate?.toFixed(2)}%</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Tax ID</div>
                        <div className="mini-stat-value">{record.taxTypeId}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record.isActive ? 'active' : 'inactive'}`}>
                            {record.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {record.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Tax Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Tax Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Tax Type:</span>
                                <span className="info-value font-semibold">{record.taxType}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Tax ID:</span>
                                <span className="info-value">{record.taxTypeId}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Rate:</span>
                                <span className="info-value font-semibold text-primary">{record.rate?.toFixed(2)}%</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag color={record.isActive ? "success" : "default"}>
                                    {record.isActive ? "Active" : "Inactive"}
                                </Tag>
                            </div>
                            {record.createdAt && (
                                <div className="info-item">
                                    <span className="info-label">Created At:</span>
                                    <span className="info-value">
                                        <DateField value={record.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            )}
                            {record.updatedAt && (
                                <div className="info-item">
                                    <span className="info-label">Last Updated:</span>
                                    <span className="info-value">
                                        <DateField value={record.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
